import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const FAQS = [
  { q: 'Which is the best CUET coaching in Assam?', a: "The right choice depends on a student's location, budget, and preferred learning format. BESST is built specifically for students across Assam, offering live classes, mock tests, and mentorship entirely online." },
  { q: 'Does Assam have dedicated CUET coaching institutes?', a: 'Dedicated, CUET-specific coaching is still limited within Assam, particularly outside major cities — which is a large part of why online options like BESST have become popular across the state.' },
  { q: 'Is online CUET coaching effective?', a: 'Yes, when it includes live classes, regular testing, and doubt resolution, online coaching can be just as effective as in-person coaching, without the added cost of travel or relocation.' },
  { q: 'Which is the best online CUET coaching in Assam?', a: "BESST offers online CUET coaching designed specifically around the needs of Assam's students, with faculty, mock tests, and mentorship accessible from any district in the state." },
  { q: 'Can students from rural Assam join BESST?', a: "Yes, BESST's coaching is fully online, so students from rural areas and smaller districts can access the same programme as students in Guwahati." },
  { q: 'Does BESST provide live online classes?', a: 'Yes, BESST conducts live, interactive classes for all CUET sections, with recordings available for revision.' },
  { q: "Are mock tests included in BESST's coaching programme?", a: 'Yes, the programme includes regular full-length and topic-wise mock tests with performance analytics.' },
  { q: 'Does BESST provide recorded classes?', a: 'Yes, every live class is recorded so students can revisit lessons at their own pace.' },
  { q: 'Is personal mentorship available?', a: "Yes, BESST provides one-to-one mentorship alongside group classes, tailored to each student's progress and weak areas." },
  { q: 'Can students from Guwahati join BESST?', a: "Yes, students from Guwahati can join BESST's online coaching and benefit from the same flexibility and mock test access as students in other districts." },
  { q: 'Can students from Jorhat join BESST?', a: "Yes, BESST's online format means students from Jorhat have full access to live classes, study materials, and mentorship without needing to relocate." },
  { q: 'Can students from Dibrugarh join BESST?', a: 'Yes, students from Dibrugarh — or any district in Assam — can enrol in BESST\'s CUET coaching programme entirely online.' },
  { q: 'Does BESST provide study materials?', a: 'Yes, comprehensive, syllabus-aligned study materials are included as part of the coaching programme.' },
  { q: "What is the fee for BESST's CUET coaching?", a: "BESST's course fees are structured to stay affordable for students across Assam. Specific fee details are available on enrolment or by reaching out directly." },
  { q: "How can I enrol in BESST's CUET Online Coaching?", a: "Students can enrol through BESST's CUET Coaching page, after which an orientation session helps determine the right course plan." },
];

const FEATURES = [
  { icon: '🎓', title: 'Expert Faculty with CUET Experience', desc: "Faculty who teach specifically to CUET's format — its language, domain, and general test sections — rather than adapting general teaching to fit the exam." },
  { icon: '📡', title: 'Live Interactive Online Classes', desc: 'Students attend live sessions and can ask questions in real time, keeping the learning experience close to a classroom rather than passive video-watching.' },
  { icon: '📚', title: 'Comprehensive Study Materials', desc: "Structured, syllabus-aligned notes and practice material, so students aren't spending time hunting for reliable resources on their own." },
  { icon: '📋', title: 'Topic-wise Practice Tests', desc: 'Focused tests after each topic help catch gaps early, rather than discovering them for the first time in a full mock test.' },
  { icon: '📝', title: 'Full-Length Mock Tests', desc: "Regular, full-length mocks that mirror CUET's actual format and timing, building genuine familiarity before exam day." },
  { icon: '📄', title: 'Previous Year Question Papers', desc: 'Dedicated PYQ practice to help students recognise recurring patterns and commonly tested areas.' },
  { icon: '🤝', title: 'Personalised Mentorship', desc: "One-to-one guidance built around each student's specific weak areas, rather than a purely group-class approach." },
  { icon: '📊', title: 'Performance Tracking', desc: 'Every test result feeds into a performance report, so progress is visible rather than assumed.' },
  { icon: '💬', title: 'Doubt Clearing Sessions', desc: 'Dedicated time outside regular classes to resolve doubts before they pile up.' },
  { icon: '🏫', title: 'Career & Admission Guidance', desc: 'Support that extends past the exam itself, into choosing the right universities and courses based on CUET performance.' },
  { icon: '💰', title: 'Affordable Course Fees', desc: "Priced with Assam's students specifically in mind, so quality coaching doesn't depend on a family's ability to afford metro-city fees." },
];

const STEPS = [
  { n: '01', title: 'Enrolment', desc: 'Enrol into the CUET Online Coaching Programme.' },
  { n: '02', title: 'Orientation', desc: "Understand the student's current level and target universities." },
  { n: '03', title: 'Live Classes', desc: 'Attend live classes covering the full CUET syllabus.' },
  { n: '04', title: 'Weekly Practice', desc: "Reinforce what's taught in class through daily and weekly exercises." },
  { n: '05', title: 'Mock Tests', desc: 'Regular mock tests run throughout the programme on a set schedule.' },
  { n: '06', title: 'Performance Review', desc: 'Detailed test analytics highlight areas that need more attention.' },
  { n: '07', title: 'Doubt Solving', desc: 'Dedicated sessions to resolve doubts before they accumulate.' },
  { n: '08', title: 'Final Revision', desc: 'Targeted revision on weak areas identified through testing.' },
  { n: '09', title: 'CUET Examination', desc: 'Approach exam day with genuine familiarity, not guesswork.' },
];

const TOC = [
  { id: 'why-matters', label: 'Why the Right Coaching Matters' },
  { id: 'challenges', label: 'Challenges in Assam' },
  { id: 'why-besst', label: 'Why BESST?' },
  { id: 'features-table', label: 'Features' },
  { id: 'why-online', label: 'Why Online for Assam?' },
  { id: 'comparison', label: 'Guwahati vs Online' },
  { id: 'who-should', label: 'Who Should Join?' },
  { id: 'how-works', label: 'How It Works' },
  { id: 'success-tips', label: 'Success Tips' },
  { id: 'faqs', label: 'FAQs' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map(f => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://besst.in' },
    { '@type': 'ListItem', position: 2, name: 'CUET Coaching', item: 'https://besst.in/cuet-coaching' },
    { '@type': 'ListItem', position: 3, name: 'Best CUET Coaching in Assam', item: 'https://besst.in/best-cuet-coaching-in-assam' },
  ],
};

const DISTRICTS = ['Guwahati', 'Jorhat', 'Dibrugarh', 'Silchar', 'Tezpur', 'Nagaon', 'Tinsukia', 'Barpeta', 'Dhubri', 'Lakhimpur'];

const CSS = `
  .sp-page { background: linear-gradient(160deg,#0c0420 0%,#140831 50%,#0c0420 100%); min-height:100vh; color:#e8deff; font-family:'Inter','Segoe UI',system-ui,sans-serif; }
  .sp-breadcrumb { max-width:1200px; margin:0 auto; padding:1rem 1.5rem 0; font-size:.8rem; color:#9ca3af; display:flex; align-items:center; gap:.4rem; flex-wrap:wrap; }
  .sp-breadcrumb a { color:#a78bfa; text-decoration:none; } .sp-breadcrumb a:hover { color:#d4af37; }
  .sp-hero { padding:3rem 1.5rem 3.5rem; max-width:860px; margin:0 auto; text-align:center; }
  .sp-badge { display:inline-block; background:rgba(212,175,55,.12); color:#d4af37; border:1px solid rgba(212,175,55,.3); border-radius:20px; padding:.35rem 1rem; font-size:.78rem; font-weight:700; letter-spacing:.08em; text-transform:uppercase; margin-bottom:1.25rem; }
  .sp-hero h1 { font-size:clamp(1.75rem,4vw,2.7rem); font-weight:800; line-height:1.2; margin:0 0 1.25rem; color:#f0e6ff; }
  .sp-hero h1 em { font-style:normal; background:linear-gradient(90deg,#d4af37,#f59e0b); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
  .sp-hero-intro { font-size:1.05rem; line-height:1.8; color:#c4b5fd; max-width:720px; margin:0 auto 2rem; }
  .sp-hero-cta { display:flex; gap:1rem; justify-content:center; flex-wrap:wrap; }
  .btn-gold { background:linear-gradient(135deg,#d4af37,#f59e0b); color:#0c0420; font-weight:700; padding:.85rem 2rem; border-radius:10px; border:none; cursor:pointer; font-size:.95rem; text-decoration:none; display:inline-block; transition:transform .2s,box-shadow .2s; }
  .btn-gold:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(212,175,55,.35); }
  .btn-outline { background:transparent; color:#e8deff; font-weight:600; padding:.85rem 2rem; border-radius:10px; border:1.5px solid rgba(232,222,255,.25); cursor:pointer; font-size:.95rem; text-decoration:none; display:inline-block; transition:border-color .2s,background .2s; }
  .btn-outline:hover { border-color:#a78bfa; background:rgba(167,139,250,.08); }
  .sp-districts { background:rgba(255,255,255,.03); border-top:1px solid rgba(255,255,255,.06); border-bottom:1px solid rgba(255,255,255,.06); padding:1.25rem 1.5rem; }
  .sp-districts-inner { max-width:1000px; margin:0 auto; text-align:center; }
  .sp-districts-label { font-size:.75rem; color:#7c6b9e; font-weight:600; letter-spacing:.08em; text-transform:uppercase; margin-bottom:.75rem; }
  .sp-district-chips { display:flex; flex-wrap:wrap; gap:.5rem; justify-content:center; }
  .sp-chip { background:rgba(124,58,237,.12); border:1px solid rgba(124,58,237,.25); border-radius:20px; padding:.3rem .85rem; font-size:.8rem; color:#c4b5fd; }
  .sp-layout { max-width:1200px; margin:0 auto; padding:3rem 1.5rem; display:grid; grid-template-columns:220px 1fr; gap:3rem; align-items:start; }
  @media(max-width:900px){.sp-layout{grid-template-columns:1fr;}}
  .sp-toc { position:sticky; top:80px; }
  @media(max-width:900px){.sp-toc{display:none;}}
  .sp-toc-title { font-size:.72rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:#7c6b9e; margin-bottom:.75rem; }
  .sp-toc ul { list-style:none; padding:0; margin:0; }
  .sp-toc li { margin-bottom:.15rem; }
  .sp-toc button { background:none; border:none; cursor:pointer; text-align:left; width:100%; padding:.4rem .6rem; font-size:.82rem; color:#9ca3af; border-radius:6px; border-left:2px solid transparent; transition:all .15s; line-height:1.4; }
  .sp-toc button:hover { color:#e8deff; background:rgba(255,255,255,.05); }
  .sp-toc button.active { color:#d4af37; border-left-color:#d4af37; background:rgba(212,175,55,.07); font-weight:600; }
  .sp-section { margin-bottom:3.5rem; scroll-margin-top:90px; }
  .sp-h2 { font-size:clamp(1.25rem,2.5vw,1.65rem); font-weight:700; color:#f0e6ff; margin:0 0 1.25rem; padding-bottom:.6rem; border-bottom:1px solid rgba(212,175,55,.2); }
  .sp-h3 { font-size:1.02rem; font-weight:700; color:#d4af37; margin:1.25rem 0 .45rem; }
  .sp-p { font-size:.97rem; line-height:1.8; color:#c4b5fd; margin:0 0 1rem; }
  .sp-bullets { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:.65rem; }
  .sp-bullets li { display:flex; gap:.75rem; align-items:flex-start; font-size:.95rem; line-height:1.65; color:#c4b5fd; }
  .sp-bi { width:22px; height:22px; background:rgba(212,175,55,.15); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:.7rem; color:#d4af37; flex-shrink:0; margin-top:2px; }
  .sp-bs { color:#e8deff; font-weight:600; }
  .sp-feature-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.85rem; }
  @media(max-width:640px){.sp-feature-grid{grid-template-columns:1fr;}}
  .sp-fcard { display:flex; gap:.85rem; align-items:flex-start; background:rgba(255,255,255,.03); border:1px solid rgba(255,255,255,.07); border-radius:10px; padding:1rem 1.1rem; transition:border-color .2s,background .2s; }
  .sp-fcard:hover { border-color:rgba(212,175,55,.25); background:rgba(255,255,255,.05); }
  .sp-ficon { font-size:1.4rem; width:42px; height:42px; display:flex; align-items:center; justify-content:center; background:rgba(212,175,55,.1); border-radius:8px; flex-shrink:0; }
  .sp-fcard h3 { font-size:.88rem; font-weight:700; color:#f0e6ff; margin:0 0 .3rem; }
  .sp-fcard p { font-size:.83rem; line-height:1.55; color:#9ca3af; margin:0; }
  .sp-table-wrap { overflow-x:auto; border-radius:12px; }
  .sp-table { width:100%; border-collapse:collapse; font-size:.9rem; }
  .sp-table thead tr { background:rgba(212,175,55,.12); }
  .sp-table th { padding:.85rem 1.25rem; text-align:left; font-weight:700; color:#d4af37; border:1px solid rgba(212,175,55,.15); font-size:.85rem; letter-spacing:.04em; }
  .sp-table td { padding:.75rem 1.25rem; border:1px solid rgba(255,255,255,.06); color:#c4b5fd; }
  .sp-table tbody tr:nth-child(even) { background:rgba(255,255,255,.025); }
  .sp-table tbody tr:hover { background:rgba(212,175,55,.05); }
  .sp-tick { color:#22c55e; font-size:1.1rem; font-weight:700; }
  .sp-cmp-table .sp-better { color:#22c55e; font-weight:600; }
  .sp-online-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:1rem; }
  @media(max-width:600px){.sp-online-grid{grid-template-columns:1fr;}}
  .sp-ocard { background:rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); border-radius:12px; padding:1.25rem; transition:border-color .2s; }
  .sp-ocard:hover { border-color:rgba(212,175,55,.3); }
  .sp-ocard-icon { font-size:1.5rem; margin-bottom:.5rem; }
  .sp-ocard h3 { font-size:.95rem; font-weight:700; color:#f0e6ff; margin:0 0 .4rem; }
  .sp-ocard p { font-size:.88rem; line-height:1.6; color:#9ca3af; margin:0; }
  .sp-steps { display:flex; flex-direction:column; }
  .sp-step { display:flex; gap:1.25rem; align-items:flex-start; }
  .sp-step-left { display:flex; flex-direction:column; align-items:center; flex-shrink:0; }
  .sp-step-num { width:44px; height:44px; border-radius:50%; background:linear-gradient(135deg,rgba(212,175,55,.25),rgba(212,175,55,.1)); border:2px solid rgba(212,175,55,.4); display:flex; align-items:center; justify-content:center; font-size:.78rem; font-weight:800; color:#d4af37; flex-shrink:0; }
  .sp-step-line { width:2px; flex:1; min-height:28px; background:linear-gradient(to bottom,rgba(212,175,55,.3),rgba(212,175,55,.05)); }
  .sp-step:last-child .sp-step-line { display:none; }
  .sp-step-body { padding:.6rem 0 1.5rem; }
  .sp-step-body h3 { font-size:.97rem; font-weight:700; color:#f0e6ff; margin:0 0 .25rem; }
  .sp-step-body p { font-size:.87rem; line-height:1.6; color:#9ca3af; margin:0; }
  .sp-tips-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.65rem; }
  @media(max-width:600px){.sp-tips-grid{grid-template-columns:1fr;}}
  .sp-tip { display:flex; gap:.65rem; align-items:flex-start; background:rgba(255,255,255,.03); border-radius:8px; padding:.75rem .9rem; font-size:.88rem; line-height:1.55; color:#c4b5fd; border:1px solid rgba(255,255,255,.06); }
  .sp-tip-check { color:#22c55e; flex-shrink:0; margin-top:1px; }
  .sp-faq-list { display:flex; flex-direction:column; gap:.6rem; }
  .sp-faq-item { background:rgba(255,255,255,.03); border:1px solid rgba(255,255,255,.08); border-radius:10px; overflow:hidden; transition:border-color .2s; }
  .sp-faq-item.open { border-color:rgba(212,175,55,.3); }
  .sp-faq-q { width:100%; background:none; border:none; cursor:pointer; display:flex; justify-content:space-between; align-items:center; gap:1rem; padding:1rem 1.25rem; text-align:left; font-size:.93rem; font-weight:600; color:#e8deff; transition:color .15s; }
  .sp-faq-q:hover { color:#d4af37; }
  .sp-faq-chevron { font-size:.75rem; color:#7c6b9e; transition:transform .25s; flex-shrink:0; }
  .sp-faq-item.open .sp-faq-chevron { transform:rotate(180deg); color:#d4af37; }
  .sp-faq-a { max-height:0; overflow:hidden; transition:max-height .3s ease; }
  .sp-faq-item.open .sp-faq-a { max-height:500px; }
  .sp-faq-a-inner { padding:.85rem 1.25rem 1rem; font-size:.88rem; line-height:1.75; color:#9ca3af; border-top:1px solid rgba(255,255,255,.06); }
  .sp-links-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:.65rem; }
  @media(max-width:540px){.sp-links-grid{grid-template-columns:1fr;}}
  .sp-ilink { display:flex; align-items:center; gap:.6rem; background:rgba(255,255,255,.03); border:1px solid rgba(255,255,255,.07); border-radius:8px; padding:.7rem 1rem; text-decoration:none; color:#a78bfa; font-size:.88rem; font-weight:500; transition:border-color .2s,background .2s; }
  .sp-ilink:hover { border-color:rgba(167,139,250,.4); background:rgba(167,139,250,.06); color:#c4b5fd; }
  .sp-cta { background:linear-gradient(135deg,rgba(124,58,237,.25) 0%,rgba(212,175,55,.12) 100%); border:1px solid rgba(212,175,55,.2); border-radius:16px; padding:2.5rem; text-align:center; margin-top:1rem; }
  .sp-cta h2 { font-size:1.5rem; font-weight:800; color:#f0e6ff; margin:0 0 .75rem; }
  .sp-cta p { font-size:.95rem; line-height:1.7; color:#c4b5fd; max-width:600px; margin:0 auto 1.5rem; }
  .sp-parent-link { display:inline-flex; align-items:center; gap:.4rem; font-size:.82rem; color:#7c6b9e; text-decoration:none; margin-bottom:.75rem; }
  .sp-parent-link:hover { color:#a78bfa; }
`;

const BestCuetCoachingAssamPage = () => {
  const [activeSection, setActiveSection] = useState('why-matters');
  const [openFaq, setOpenFaq] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setActiveSection(e.target.id); }); },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    TOC.forEach(t => { const el = document.getElementById(t.id); if (el) observerRef.current.observe(el); });
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <>
      <Helmet>
        <title>Best CUET Coaching in Assam | BESST Online Coaching</title>
        <meta name="description" content="Looking for the best CUET coaching in Assam? Join BESST's expert-led online coaching with live classes, mock tests, study materials and personalised mentorship." />
        <meta name="keywords" content="best cuet coaching in assam, cuet coaching assam, cuet coaching guwahati, online cuet coaching in assam, cuet coaching classes assam, best online cuet coaching, cuet coaching for assam students, cuet preparation in assam" />
        <meta property="og:title" content="Best CUET Coaching in Assam | BESST Online Coaching" />
        <meta property="og:description" content="Looking for the best CUET coaching in Assam? Join BESST's expert-led online coaching with live classes, mock tests, study materials and personalised mentorship." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://besst.in/best-cuet-coaching-in-assam" />
        <link rel="canonical" href="https://besst.in/best-cuet-coaching-in-assam" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="sp-page mt-20">
        <style>{CSS}</style>

        {/* Breadcrumb */}
        <nav className="sp-breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span>›</span>
          <Link to="/cuet-coaching">CUET Coaching</Link>
          <span>›</span>
          <span style={{ color: '#e8deff' }}>Best CUET Coaching in Assam</span>
        </nav>

        {/* Hero */}
        <div className="sp-hero">
          <div className="sp-badge">For Students Across Assam</div>
          <h1>Best <em>CUET Coaching in Assam</em> – Prepare for CUET with BESST's Expert Online Coaching</h1>
          <p className="sp-hero-intro">
            Every year, more students across Assam are setting their sights on CUET as the gateway to central universities and top undergraduate programmes across the country. BESST brings expert-led CUET coaching entirely online — so a student in Dibrugarh has access to the same faculty, mock tests, and mentorship as a student sitting in a metro coaching centre.
          </p>
          <div className="sp-hero-cta">
            <Link to="/subscriptions" className="btn-gold">Start Your CUET Journey →</Link>
            <Link to="/cuet-mock-test" className="btn-outline">Try a Free Mock Test</Link>
          </div>
        </div>

        {/* Districts strip */}
        <div className="sp-districts">
          <div className="sp-districts-inner">
            <div className="sp-districts-label">Available across every district in Assam</div>
            <div className="sp-district-chips">
              {DISTRICTS.map(d => <span key={d} className="sp-chip">📍 {d}</span>)}
            </div>
          </div>
        </div>

        {/* Layout */}
        <div className="sp-layout">

          {/* TOC */}
          <aside className="sp-toc" aria-label="Table of contents">
            <div className="sp-toc-title">On This Page</div>
            <ul>
              {TOC.map(t => (
                <li key={t.id}>
                  <button className={activeSection === t.id ? 'active' : ''} onClick={() => scrollTo(t.id)}>{t.label}</button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main */}
          <main>

            {/* Parent link */}
            <Link to="/cuet-coaching" className="sp-parent-link">← Back to CUET Coaching</Link>

            {/* Intro para */}
            <p className="sp-p" style={{ marginBottom: '2rem' }}>
              BESST is among the best CUET coaching providers for students across Assam, offering fully online preparation with expert faculty, regular mock tests, and personalised mentorship. Students from any district — Guwahati, Jorhat, Dibrugarh, Silchar, and beyond — can access the same structured coaching without relocating or commuting.
            </p>

            {/* Why it matters */}
            <section id="why-matters" className="sp-section">
              <h2 className="sp-h2">Why Choosing the Right CUET Coaching in Assam Matters</h2>
              <p className="sp-p">CUET preparation isn't just about covering the syllabus — it's about covering it well, with feedback and structure, in a limited window of time. For students in Assam specifically, a few factors make choosing the right coaching partner especially important:</p>
              <ul className="sp-bullets">
                {[
                  ['Rising competition', 'More students from Assam are appearing for CUET each year, aiming for central universities and top programmes that were historically harder to access from the region.'],
                  ['Limited quality coaching outside major cities', 'Dedicated CUET coaching is still concentrated in a few urban centres, leaving students in smaller districts with fewer reliable options nearby.'],
                  ['Need for structured preparation', "Without a set plan, it's easy for a wide, multi-section syllabus to fall out of balance in the weeks before the exam."],
                  ['Importance of expert guidance', "A faculty member who understands CUET's exact format and marking scheme can help a student avoid the same avoidable mistakes year after year."],
                ].map(([t, d]) => (
                  <li key={t}><span className="sp-bi">✓</span><span><span className="sp-bs">{t}: </span>{d}</span></li>
                ))}
              </ul>
              <p className="sp-p" style={{ marginTop: '1rem' }}>The right coaching, in other words, isn't a convenience — for many Assam students, it's the difference between preparing with direction and preparing in the dark.</p>
            </section>

            {/* Challenges */}
            <section id="challenges" className="sp-section">
              <h2 className="sp-h2">Challenges Faced by CUET Aspirants in Assam</h2>
              {[
                ['Limited Availability of Dedicated CUET Coaching', 'Most coaching institutes in Assam are built around board exams or older entrance formats, and dedicated, up-to-date CUET coaching is still relatively rare outside a few cities.'],
                ['Travelling to Metro Cities is Expensive', 'For families outside Guwahati, accessing established coaching often means travel or relocation costs — an added financial burden on top of course fees.'],
                ['Lack of Experienced CUET Faculty', 'CUET is a relatively newer exam format, and experienced, CUET-specific faculty are harder to find locally than faculty for older, more established entrance exams.'],
                ['Limited Access to Mock Tests', "Without a coaching institute running structured mock tests, students are often left practising with generic material that doesn't reflect CUET's actual format and timing."],
                ['Difficulty Balancing Boards and CUET Preparation', 'Class XII students are already managing a full board exam workload — fitting in separate, unstructured CUET preparation on top of that is genuinely difficult without a plan built around both.'],
              ].map(([h, p]) => (
                <div key={h}>
                  <h3 className="sp-h3">{h}</h3>
                  <p className="sp-p">{p}</p>
                </div>
              ))}
              <p className="sp-p" style={{ background: 'rgba(212,175,55,.07)', border: '1px solid rgba(212,175,55,.2)', borderRadius: 10, padding: '1rem 1.25rem' }}>
                💡 BESST's online model addresses every one of these directly: there's no travel involved, faculty are dedicated CUET specialists, mock tests are built into the programme, and study schedules are structured to work alongside board exam preparation rather than compete with it.
              </p>
            </section>

            {/* Why BESST */}
            <section id="why-besst" className="sp-section">
              <h2 className="sp-h2">Why BESST is One of the Best CUET Coaching Institutes in Assam</h2>
              <div className="sp-feature-grid">
                {FEATURES.map(f => (
                  <div key={f.title} className="sp-fcard">
                    <div className="sp-ficon">{f.icon}</div>
                    <div><h3>{f.title}</h3><p>{f.desc}</p></div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
                <Link to="/subscriptions" className="btn-gold">Explore BESST's CUET Online Coaching Programme →</Link>
              </div>
            </section>

            {/* Features table */}
            <section id="features-table" className="sp-section">
              <h2 className="sp-h2">BESST's CUET Online Coaching Features</h2>
              <div className="sp-table-wrap">
                <table className="sp-table">
                  <thead><tr><th>Feature</th><th>BESST Online Coaching</th></tr></thead>
                  <tbody>
                    {['Live Classes','Recorded Lectures','Mock Tests','PYQ Practice','Performance Analytics','Personal Mentorship','Doubt Solving','Digital Study Materials','Mobile Learning','Admission Guidance'].map(f => (
                      <tr key={f}><td>{f}</td><td><span className="sp-tick">✔</span></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Why online for Assam */}
            <section id="why-online" className="sp-section">
              <h2 className="sp-h2">Why Online CUET Coaching is Better for Students Across Assam</h2>
              <div className="sp-online-grid">
                {[
                  ['🗺️', 'Learn from Any District in Assam', "BESST's online format means location is no longer a barrier. Students from Guwahati, Jorhat, Dibrugarh, Silchar, Tezpur, Nagaon, Tinsukia, and every other district get access to the same faculty, classes, and mock tests."],
                  ['🏠', 'No Need to Relocate', 'Students can prepare seriously for CUET without moving away from home, board-exam school routines, or family support systems during a genuinely stressful year.'],
                  ['🕐', 'Flexible Learning Schedule', 'Live classes can be scheduled around board exam study time rather than competing with it.'],
                  ['🎬', 'Learn at Your Own Pace', 'Recorded sessions mean students can revisit a topic as many times as they need before a mock test or the exam itself.'],
                  ['💰', 'Save Time and Travel Costs', 'No daily commute, no relocation expenses — just direct access to structured coaching from home.'],
                  ['📱', 'Mobile Learning', 'Access classes, mock tests, and study materials on any device, from anywhere with an internet connection.'],
                ].map(([icon, title, desc]) => (
                  <div key={title} className="sp-ocard">
                    <div className="sp-ocard-icon">{icon}</div>
                    <h3>{title}</h3>
                    <p>{desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Comparison table */}
            <section id="comparison" className="sp-section">
              <h2 className="sp-h2">CUET Coaching in Guwahati vs Online Coaching – Which is Better?</h2>
              <div className="sp-table-wrap">
                <table className="sp-table sp-cmp-table">
                  <thead><tr><th>Factors</th><th>Offline Coaching (Guwahati)</th><th>BESST Online Coaching</th></tr></thead>
                  <tbody>
                    {[
                      ['Accessibility', 'Limited to one city', 'Available across Assam'],
                      ['Faculty', 'Depends on institute', 'Expert CUET mentors'],
                      ['Recorded Classes', 'Usually unavailable', 'Available'],
                      ['Mock Tests', 'Limited', 'Unlimited practice'],
                      ['Travel Time', 'High', 'None'],
                      ['Cost', 'Higher', 'Affordable'],
                      ['Flexibility', 'Limited', 'Learn anytime'],
                    ].map(([factor, offline, online]) => (
                      <tr key={factor}>
                        <td style={{ fontWeight: 600, color: '#e8deff' }}>{factor}</td>
                        <td style={{ color: '#9ca3af' }}>{offline}</td>
                        <td className="sp-better">{online}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="sp-p" style={{ marginTop: '1rem' }}>For students already based in Guwahati, offline coaching is a genuine option — but even there, online coaching through BESST tends to offer more flexibility, more mock test access, and lower overall cost, without any drop in faculty quality.</p>
            </section>

            {/* Who should join */}
            <section id="who-should" className="sp-section">
              <h2 className="sp-h2">Who Should Join BESST's CUET Coaching?</h2>
              <ul className="sp-bullets">
                {[
                  'Class XII students across Assam preparing for CUET alongside board exams',
                  'Repeaters aiming to improve on a previous CUET attempt',
                  'Rural students without access to dedicated coaching nearby',
                  'Students preparing with Board Exams who need a schedule that respects limited study time',
                  'Students aiming for Central Universities who want focused, high-intent preparation',
                ].map(t => <li key={t}><span className="sp-bi">→</span><span>{t}</span></li>)}
              </ul>
            </section>

            {/* How it works */}
            <section id="how-works" className="sp-section">
              <h2 className="sp-h2">BESST's Step-by-Step Learning Process</h2>
              <div className="sp-steps">
                {STEPS.map(s => (
                  <div key={s.n} className="sp-step">
                    <div className="sp-step-left">
                      <div className="sp-step-num">{s.n}</div>
                      <div className="sp-step-line" />
                    </div>
                    <div className="sp-step-body"><h3>{s.title}</h3><p>{s.desc}</p></div>
                  </div>
                ))}
              </div>
            </section>

            {/* Tips */}
            <section id="success-tips" className="sp-section">
              <h2 className="sp-h2">Success Tips for Assam Students Preparing for CUET</h2>
              <div className="sp-tips-grid">
                {[
                  'Follow NCERT closely for domain subject preparation',
                  'Practice mock tests regularly, not just in the final month',
                  'Build a study schedule that accounts for board exam commitments',
                  'Work consistently on improving speed alongside accuracy',
                  'Analyse mistakes after every test rather than only tracking scores',
                  'Stay consistent — steady preparation over months outperforms last-minute cramming',
                ].map(tip => (
                  <div key={tip} className="sp-tip"><span className="sp-tip-check">✓</span><span>{tip}</span></div>
                ))}
              </div>
            </section>

            {/* Why trust */}
            <section className="sp-section">
              <h2 className="sp-h2">Why Students Across Assam Trust BESST</h2>
              <ul className="sp-bullets">
                {[
                  "Student-centric learning approach built around each student's pace and needs",
                  'Affordable coaching designed for accessibility across the region',
                  'Expert mentors with genuine CUET-specific experience',
                  'Interactive learning through live classes, not passive content',
                  'Continuous assessment so progress is tracked, not assumed',
                  'Admission guidance that carries students through to university selection',
                  'Flexible online platform accessible from anywhere in Assam',
                ].map(t => <li key={t}><span className="sp-bi">✓</span><span>{t}</span></li>)}
              </ul>
            </section>

            {/* FAQs */}
            <section id="faqs" className="sp-section">
              <h2 className="sp-h2">Frequently Asked Questions (FAQs)</h2>
              <div className="sp-faq-list">
                {FAQS.map((f, i) => (
                  <div key={i} className={`sp-faq-item ${openFaq === i ? 'open' : ''}`}>
                    <button className="sp-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                      <span>{f.q}</span>
                      <span className="sp-faq-chevron">▼</span>
                    </button>
                    <div className="sp-faq-a" aria-hidden={openFaq !== i}>
                      <div className="sp-faq-a-inner">{f.a}</div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Internal links */}
            <section className="sp-section">
              <h2 className="sp-h2">Explore More CUET Resources</h2>
              <div className="sp-links-grid">
                {[
                  ['/cuet-coaching', '📚 CUET Coaching (Pillar)'],
                  ['/cuet-mock-test', '📝 CUET Mock Test'],
                  ['/cuet-mock-test/free', '🆓 Free CUET Mock Test'],
                  ['/cuet-syllabus', '📋 CUET Syllabus'],
                  ['/cuet-exam-pattern', '📐 CUET Exam Pattern'],
                  ['/cuet-registration', '📝 CUET Registration'],
                  ['/cuet-colleges', '🏫 CUET Colleges'],
                  ['/cuet-cutoff', '📊 CUET Cutoff'],
                  ['/cuet-counselling', '🎓 CUET Counselling'],
                  ['/cuet-previous-year-question-papers', '📄 CUET PYQ Papers'],
                  ['/cuet-preparation-strategy', '🎯 CUET Preparation Strategy'],
                  ['/cuet-study-plan', '🗓️ CUET Study Plan'],
                  ['/cuet-subject-list', '📑 CUET Subject List'],
                ].map(([to, label]) => (
                  <Link key={to} to={to} className="sp-ilink">
                    <span>{label}</span>
                    <span style={{ marginLeft: 'auto', opacity: .5 }}>→</span>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <div className="sp-cta">
              <h2>Start Your CUET Preparation with BESST Today</h2>
              <p>
                Choosing the right coaching matters — for students across Assam, it can be the difference between preparing with structure and guidance, or piecing together a plan alone. BESST brings expert faculty, live classes, comprehensive study materials, and personalised mentorship to students in every district.
              </p>
              <Link to="/subscriptions" className="btn-gold">Join BESST's CUET Online Coaching →</Link>
            </div>

          </main>
        </div>
      </div>
    </>
  );
};

export default BestCuetCoachingAssamPage;
